/**
 * antepost — the dedicated outright view (AntepostPage), opened from the sidebar
 * button, separate from the sport tree.
 *
 * Navigation: sport (chips) → category (accordion) → tournament → event (markets).
 * The selection (sportId / categoryId / tournamentId / eventId) is the page state:
 * sportNavigation derives the `antepost` page and its params from it, so the slug
 * sent to the skin, the URL and a shared link always name what is on screen.
 * A tournament link without an event opens the tournament's first event.
 *
 * Memory: the event payload comes with every competitor and its full squad
 * (~95% of the bytes), which the outright view never reads: it is dropped. Only the
 * open event is kept; categories and tournaments are small and cached with a cap
 * (LRU). Everything is frozen: there are no socket updates on outrights, so Vue
 * does not need to observe it.
 */
import axios from 'axios'

export const CATEGORIES_CACHE_MAX = 4
export const TOURNAMENTS_CACHE_MAX = 16
export const AVAILABILITY_CACHE_MAX = 16

const state = () => ({
    open: false,
    sportId: null,
    categoryId: null,
    tournamentId: null,
    eventId: null,

    sports: [],
    sportsLoaded: false,
    loadingSports: false,

    // { [sportId]: Category[] }, insertion order = recency
    categories: {},
    loadingCategories: false,

    // { 'sportId|categoryId': Tournament[] }, insertion order = recency
    tournaments: {},
    loadingTournaments: {},

    event: null,
    loadingEvent: false,
    eventError: false,

    // { 'sportId|categoryId': [tournamentId] } — which prematch tournaments have an outright
    // (same ids on both sides), for the Antepost tab of the tournament preview. Ids only,
    // LRU-capped, kept when the view closes: the preview asks for it on every tournament.
    available: {},

    // Tournament preview on its "Antepost" tab (MarketViewV3Classic switch):
    // { sportId, categoryId, tournamentId, eventId } or null. Part of the page state
    // (sportNavigation page `tournament_antepost`), so the URL keeps the tab and the season.
    // The event shown in the tab lives in the panel component, freed with it.
    tournamentTab: null,
})

export const tournamentsKey = (sportId, categoryId) => `${sportId}|${categoryId}`

const str = v => (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') ? null : String(v)

/** Insert in an LRU map (plain object, insertion order), dropping the oldest keys over `max`. */
export function lruSet(map, key, value, max) {
    const next = {}
    Object.keys(map).forEach(k => { if (k !== key) next[k] = map[k] })
    next[key] = value
    const keys = Object.keys(next)
    keys.slice(0, Math.max(0, keys.length - max)).forEach(k => { delete next[k] })
    return next
}

/** Market lines as served (flat or grouped by specifier) → flat list. */
function flattenMarkets(odds) {
    if (!Array.isArray(odds)) return []
    return odds.reduce((all, m) => all.concat(Array.isArray(m) ? m : [m]), []).filter(m => m && Array.isArray(m.outcome))
}

/**
 * Event payload → what the view needs. Drops `competitors` (squads), `source`,
 * `_id`s; the odd objects are kept as served because the cart reads them.
 */
export function slimAntepostEvent(event) {
    if (!event) return null
    // Most selections first: the winner / top scorer markets (a whole league of runners) lead,
    // the one-line specials follow. Language-independent; stable for equal counts.
    const markets = flattenMarkets(event.odds)
        .map((m, i) => ({ m, i }))
        .sort((a, b) => (b.m.outcome.length - a.m.outcome.length) || (a.i - b.i))
        .map(({ m }) => m)
        .map(m => Object.freeze({
        ...m,
        outcome: Object.freeze(m.outcome.map(o => Object.freeze({ ...o }))),
    }))
    const associatedEvents = (event.associatedEvents || [])
        .map(e => ({ eventId: String(e.eventId), eventName: e.eventName, scheduledDate: e.scheduledDate }))
        .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
    return Object.freeze({
        id: event.id,
        name: event.name || event.eventName || null,
        scheduled: event.scheduled,
        active: event.active,
        quickbet: event.quickbet,
        earlyPayout: event.earlyPayout,
        earlyPayoutOutcomes: event.earlyPayoutOutcomes,
        earlyPayoutMargin: event.earlyPayoutMargin,
        sport: Object.freeze({ id: String(event.sport.id), name: event.sport.name }),
        category: Object.freeze({ id: String(event.category.id), name: event.category.name }),
        tournament: Object.freeze({ id: String(event.tournament.id), name: event.tournament.name }),
        markets: Object.freeze(markets),
        associatedEvents: Object.freeze(associatedEvents),
    })
}

/** The tournament an event belongs to, among the cached tournaments of a category. */
function findTournament(list, tournamentId, eventId) {
    if (!list) return null
    if (tournamentId) {
        const t = list.find(t => String(t.tournamentId) === String(tournamentId))
        if (t) return t
    }
    if (eventId) return list.find(t => (t.events || []).some(e => String(e.eventId) === String(eventId))) || null
    return null
}

const getters = {
    isOpen: s => s.open,
    selection: s => ({ sportId: s.sportId, categoryId: s.categoryId, tournamentId: s.tournamentId, eventId: s.eventId }),
    sports: s => s.sports,
    sportsLoaded: s => s.sportsLoaded,
    loadingSports: s => s.loadingSports,
    currentSport: s => s.sports.find(sp => String(sp.sportId) === String(s.sportId)) || null,
    categories: s => s.categories[s.sportId] || [],
    loadingCategories: s => s.loadingCategories,
    tournamentsFor: s => categoryId => s.tournaments[tournamentsKey(s.sportId, categoryId)] || null,
    isLoadingTournaments: s => categoryId => !!s.loadingTournaments[tournamentsKey(s.sportId, categoryId)],
    event: s => s.event,
    /** true / false, or null while the tournament's category was not checked yet. */
    hasAntepostFor: s => (sportId, categoryId, tournamentId) => {
        const ids = s.available[tournamentsKey(sportId, categoryId)]
        return ids ? ids.includes(String(tournamentId)) : null
    },
    loadingEvent: s => s.loadingEvent,
    tournamentTab: s => s.tournamentTab,
    isTournamentTab: s => tournamentId => !!s.tournamentTab && String(s.tournamentTab.tournamentId) === String(tournamentId),
    eventError: s => s.eventError,

    /** Events of the open tournament (seasons / races), for the switcher above the markets. */
    siblingEvents(s) {
        if (s.event && s.event.associatedEvents.length > 1) return s.event.associatedEvents
        const t = findTournament(s.tournaments[tournamentsKey(s.sportId, s.categoryId)], s.tournamentId, s.eventId)
        return t && t.events && t.events.length > 1 ? t.events : []
    },

    /** Params for sportNavigation (slug / URL). Names only when known. */
    navParams(s) {
        const sport = s.sports.find(sp => String(sp.sportId) === String(s.sportId))
        const ev = s.event && String(s.event.id) === String(s.eventId) ? s.event : null
        let eventName = null
        let tournamentName = ev ? ev.tournament.name : null
        if (s.eventId) {
            const t = findTournament(s.tournaments[tournamentsKey(s.sportId, s.categoryId)], s.tournamentId, s.eventId)
            const e = t && (t.events || []).find(e => String(e.eventId) === String(s.eventId))
            if (e) eventName = e.eventName
            if (!tournamentName && t) tournamentName = t.name
        }
        return {
            sportId: s.sportId,
            categoryId: s.categoryId,
            tournamentId: s.tournamentId,
            eventId: s.eventId,
            sportName: ev ? ev.sport.name : (sport ? sport.name : null),
            tournamentName,
            eventName,
        }
    },
}

const mutations = {
    SET_OPEN(s, open) { s.open = open },
    SET_SELECTION(s, { sportId, categoryId, tournamentId, eventId }) {
        s.sportId = str(sportId)
        s.categoryId = str(categoryId)
        s.tournamentId = str(tournamentId)
        s.eventId = str(eventId)
    },
    SET_SPORTS(s, sports) {
        s.sports = Object.freeze(sports)
        s.sportsLoaded = true
    },
    SET_LOADING_SPORTS(s, v) { s.loadingSports = v },
    SET_CATEGORIES(s, { sportId, categories }) {
        s.categories = lruSet(s.categories, String(sportId), Object.freeze(categories), CATEGORIES_CACHE_MAX)
    },
    SET_LOADING_CATEGORIES(s, v) { s.loadingCategories = v },
    SET_TOURNAMENTS(s, { key, tournaments }) {
        s.tournaments = lruSet(s.tournaments, key, Object.freeze(tournaments), TOURNAMENTS_CACHE_MAX)
    },
    SET_LOADING_TOURNAMENTS(s, { key, value }) {
        const next = { ...s.loadingTournaments }
        if (value) next[key] = true
        else delete next[key]
        s.loadingTournaments = next
    },
    SET_AVAILABLE(s, { key, ids }) {
        s.available = lruSet(s.available, key, Object.freeze(ids), AVAILABILITY_CACHE_MAX)
    },
    SET_TOURNAMENT_TAB(s, tab) {
        s.tournamentTab = tab ? Object.freeze({
            sportId: str(tab.sportId),
            categoryId: str(tab.categoryId),
            tournamentId: str(tab.tournamentId),
            eventId: str(tab.eventId),
        }) : null
    },
    SET_EVENT(s, event) { s.event = event },
    SET_LOADING_EVENT(s, v) { s.loadingEvent = v },
    SET_EVENT_ERROR(s, v) { s.eventError = v },
    /** Closing the view frees everything but the sports list (a few rows, reused at the next open). */
    RESET(s) {
        s.open = false
        s.sportId = s.categoryId = s.tournamentId = s.eventId = null
        s.categories = {}
        s.tournaments = {}
        s.loadingTournaments = {}
        s.event = null
        s.loadingEvent = false
        s.eventError = false
    },
}

function api(rootGetters) {
    return {
        url: rootGetters['settings/getUrlApi'],
        headers: { headers: { Authorization: `Bearer ${rootGetters['settings/getToken']}` } },
        base: { language: rootGetters['settings/getLanguage'], platformId: rootGetters['settings/getPlatformId'] },
    }
}

// Requests that were overtaken by a newer navigation are dropped on arrival.
let eventSeq = 0
// Tournament lists in flight, by key: several previews / the view share one call.
const tournamentsInFlight = new Map()

const tournamentIds = tournaments => tournaments.map(t => String(t.tournamentId))

const actions = {
    /**
     * Opens the view, optionally on a sport / category / tournament / event (deep link,
     * slug restore, history). Closes whatever the center column was showing: the view
     * replaces it, and goBack re-opens it from the history entry.
     */
    async open({ commit, dispatch, state, rootGetters }, payload = {}) {
        if (!payload.skipHistory && !state.open) {
            await dispatch('sportNavigation/pushHistory', null, { root: true })
        }
        await dispatch('closeOtherViews')
        commit('SET_SELECTION', {
            sportId: payload.sportId,
            categoryId: payload.categoryId,
            tournamentId: payload.tournamentId,
            eventId: payload.eventId,
        })
        commit('SET_OPEN', true)
        await dispatch('ui/setViewMode', { mode: 'preview', skipHistory: true }, { root: true })
        await dispatch('load')
    },

    /** Re-applies a history entry while the view is open (back inside the view). */
    async restore({ commit, dispatch }, params = {}) {
        commit('SET_SELECTION', params)
        await dispatch('load')
    },

    /** Loads what the current selection needs: sports, the sport's categories, the category's tournaments, the event. */
    async load({ state, commit, dispatch, getters }) {
        await dispatch('fetchSports')
        if (!state.sportId && state.sports.length) {
            commit('SET_SELECTION', { ...getters.selection, sportId: state.sports[0].sportId })
        }
        if (!state.sportId) return

        const sportId = state.sportId
        const jobs = [dispatch('fetchCategories', { sportId })]
        if (state.categoryId) jobs.push(dispatch('fetchTournaments', { sportId, categoryId: state.categoryId }))
        await Promise.all(jobs)
        if (state.sportId !== sportId) return

        // Tournament link without an event: its first event.
        if (state.tournamentId && !state.eventId && state.categoryId) {
            const t = findTournament(state.tournaments[tournamentsKey(sportId, state.categoryId)], state.tournamentId)
            if (t && t.events && t.events[0]) {
                commit('SET_SELECTION', { ...getters.selection, eventId: t.events[0].eventId })
            }
        }
        if (state.eventId) await dispatch('fetchEvent')
        else commit('SET_EVENT', null)
    },

    async fetchSports({ state, commit, rootGetters }) {
        if (state.sportsLoaded || state.loadingSports) return
        const { url, headers, base } = api(rootGetters)
        commit('SET_LOADING_SPORTS', true)
        try {
            // filter 0: the outright list does not follow the sidebar's time filter.
            const res = await axios.post(`${url}/v3/navigation/tree/v4/get-sports`, { ...base, filter: 0 }, headers)
            const sports = ((res.data && res.data.sportsAntepost) || [])
                .filter(s => s && s.count > 0)
                .map(s => ({ sportId: String(s.sportId), name: s.name, count: s.count, order: s.order }))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
            commit('SET_SPORTS', sports)
        } catch (e) {
            console.error('[antepost] fetchSports', e)
        } finally {
            commit('SET_LOADING_SPORTS', false)
        }
    },

    async fetchCategories({ state, commit, rootGetters }, { sportId }) {
        if (state.categories[sportId]) {
            // Touch: keeps the sport most recent in the LRU.
            commit('SET_CATEGORIES', { sportId, categories: state.categories[sportId] })
            return
        }
        const { url, headers, base } = api(rootGetters)
        const sport = state.sports.find(s => s.sportId === String(sportId))
        commit('SET_LOADING_CATEGORIES', true)
        try {
            const res = await axios.post(`${url}/v3/navigation/tree/v4/get-category-for-sport-antepost`,
                { ...base, sportId, filter: 0, sportName: sport ? sport.name : undefined }, headers)
            const categories = ((res.data && res.data.categories) || [])
                .map(c => ({ categoryId: String(c.categoryId), name: c.name, count: c.count, order: c.order }))
            commit('SET_CATEGORIES', { sportId, categories })
        } catch (e) {
            console.error('[antepost] fetchCategories', e)
        } finally {
            commit('SET_LOADING_CATEGORIES', false)
        }
    },

    /**
     * Tournaments (with their events) of a sport|category. Also records which tournaments
     * have an outright (`available`). Concurrent callers share one request.
     */
    fetchTournaments({ state, commit, rootGetters }, { sportId, categoryId }) {
        const key = tournamentsKey(sportId, categoryId)
        if (state.tournaments[key]) return Promise.resolve()
        if (tournamentsInFlight.has(key)) return tournamentsInFlight.get(key)
        const { url, headers, base } = api(rootGetters)
        const sport = state.sports.find(s => s.sportId === String(sportId))
        const category = (state.categories[sportId] || []).find(c => c.categoryId === String(categoryId))
        commit('SET_LOADING_TOURNAMENTS', { key, value: true })
        const request = axios.post(`${url}/v3/navigation/tree/v4/get-tournaments-for-sport-category-antepost`, {
            ...base, sportId: String(sportId), categoryId: String(categoryId), filter: 0,
            sportName: sport ? sport.name : undefined,
            categoryName: category ? category.name : undefined,
        }, headers)
            .then(res => {
                const tournaments = ((res.data && res.data.tournaments) || []).map(t => ({
                    tournamentId: String(t.tournamentId),
                    name: t.name,
                    order: t.order,
                    events: (t.events || [])
                        .filter(e => e.hasOdds !== false)
                        .map(e => ({ eventId: String(e.eventId), eventName: e.eventName, scheduledDate: e.scheduledDate })),
                })).filter(t => t.events.length > 0)
                commit('SET_TOURNAMENTS', { key, tournaments })
                commit('SET_AVAILABLE', { key, ids: tournamentIds(tournaments) })
            })
            .catch(e => {
                // Not cached: the next caller asks again.
                console.error('[antepost] fetchTournaments', e)
            })
            .finally(() => {
                tournamentsInFlight.delete(key)
                commit('SET_LOADING_TOURNAMENTS', { key, value: false })
            })
        tournamentsInFlight.set(key, request)
        return request
    },

    /** Which tournaments of a category have an outright with odds (switch of the tournament preview). */
    async checkAvailability({ state, dispatch }, payload) {
        const sportId = str(payload.sportId)
        const categoryId = str(payload.categoryId)
        if (!sportId || !categoryId) return
        if (state.available[tournamentsKey(sportId, categoryId)]) return
        await dispatch('fetchTournaments', { sportId, categoryId })
    },

    // ── Antepost tab of the tournament preview ───────────────────────────────

    /** Switch "Quote partita | Antepost" → Antepost. A navigation step (back returns to the matches). */
    async showTournamentTab({ commit, dispatch }, payload) {
        if (!payload.skipHistory) await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_TOURNAMENT_TAB', payload)
    },

    /** Switch → match odds. */
    async hideTournamentTab({ state, commit, dispatch }, payload = {}) {
        if (!state.tournamentTab) return
        if (!payload.skipHistory) await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_TOURNAMENT_TAB', null)
    },

    /** Season chip inside the tab. A navigation step: the URL names the season. */
    async selectTournamentSeason({ state, commit, dispatch }, { eventId }) {
        if (!state.tournamentTab || String(state.tournamentTab.eventId) === String(eventId)) return
        await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_TOURNAMENT_TAB', { ...state.tournamentTab, eventId })
    },

    /** The preview closed or moved to another tournament: drop a tab left behind, without a history step. */
    async clearTournamentTab({ state, commit }, { keepTournamentIds = [] } = {}) {
        if (state.tournamentTab && !keepTournamentIds.map(String).includes(state.tournamentTab.tournamentId)) {
            commit('SET_TOURNAMENT_TAB', null)
        }
    },

    /** Seasons (events) of an outright tournament, from the same LRU-capped cache as the view. */
    async getTournamentEvents({ state, dispatch }, { sportId, categoryId, tournamentId }) {
        const sid = str(sportId), cid = str(categoryId)
        if (!sid || !cid) return []
        await dispatch('fetchTournaments', { sportId: sid, categoryId: cid })
        const t = findTournament(state.tournaments[tournamentsKey(sid, cid)], tournamentId)
        return t ? t.events : []
    },

    /** One outright event, slimmed and frozen, returned to the caller (not stored). Null when unavailable. */
    async loadEventDetail({ rootGetters }, { eventId, sportId, categoryId, tournamentId }) {
        const { url, headers, base } = api(rootGetters)
        const res = await axios.post(`${url}/v3/events/antepost/event-detail`,
            { ...base, eventId, sportId, categoryId, tournamentId }, headers)
        const ok = res.data && res.data.messages === 'Success Operation' && res.data.event
        return ok ? slimAntepostEvent(res.data.event) : null
    },

    async fetchEvent({ state, commit, rootGetters }) {
        const seq = ++eventSeq
        const { eventId, sportId, categoryId, tournamentId } = state
        if (state.event && String(state.event.id) === String(eventId)) return
        const { url, headers, base } = api(rootGetters)
        commit('SET_LOADING_EVENT', true)
        commit('SET_EVENT_ERROR', false)
        try {
            const res = await axios.post(`${url}/v3/events/antepost/event-detail`,
                { ...base, eventId, sportId, categoryId, tournamentId }, headers)
            if (seq !== eventSeq) return
            const ok = res.data && res.data.messages === 'Success Operation' && res.data.event
            const event = ok ? slimAntepostEvent(res.data.event) : null
            commit('SET_EVENT', event)
            commit('SET_EVENT_ERROR', !event)
            // A link may carry only the event: fill in where it lives, for the URL and the breadcrumb.
            if (event && seq === eventSeq) {
                commit('SET_SELECTION', {
                    sportId: state.sportId || event.sport.id,
                    categoryId: state.categoryId || event.category.id,
                    tournamentId: state.tournamentId || event.tournament.id,
                    eventId: state.eventId,
                })
            }
        } catch (e) {
            if (seq !== eventSeq) return
            console.error('[antepost] fetchEvent', e)
            commit('SET_EVENT', null)
            commit('SET_EVENT_ERROR', true)
        } finally {
            if (seq === eventSeq) commit('SET_LOADING_EVENT', false)
        }
    },

    /** Sport chip. A navigation step: back returns to the previous sport. */
    async selectSport({ state, commit, dispatch }, { sportId }) {
        if (String(sportId) === String(state.sportId) && !state.eventId) return
        await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_SELECTION', { sportId })
        commit('SET_EVENT', null)
        await dispatch('load')
    },

    /** Event row / season switcher. A navigation step. */
    async openEvent({ state, commit, dispatch }, { categoryId, tournamentId, eventId }) {
        if (String(eventId) === String(state.eventId)) return
        await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_SELECTION', { sportId: state.sportId, categoryId, tournamentId, eventId })
        await dispatch('fetchEvent')
    },

    /** Back from the markets to the list of the same sport (the in-view arrow). */
    async showList({ state, commit, dispatch, rootGetters }) {
        const prev = rootGetters['sportNavigation/getPreviousPage']
        if (prev && prev.page === 'antepost' && !prev.params.eventId && String(prev.params.sportId) === String(state.sportId)) {
            // The list is the previous history entry: a real back, so browser history stays in step.
            await dispatch('sportNavigation/goBack', null, { root: true })
            return
        }
        await dispatch('sportNavigation/pushHistory', null, { root: true })
        commit('SET_SELECTION', { sportId: state.sportId, categoryId: state.categoryId })
        commit('SET_EVENT', null)
    },

    async close({ commit }) {
        eventSeq++
        commit('RESET')
    },

    /** Prematch / live details, tournament previews and the prelive panel make room for the view. */
    async closeOtherViews({ commit, dispatch, rootGetters }) {
        const liveDetail = rootGetters['detailLive/getEventsLiveDetail'] || []
        for (const d of liveDetail) {
            const id = (d.event && d.event.id) || d.source
            if (id) await dispatch('detailLive/removeEventLiveDetail', id, { root: true })
        }
        await dispatch('detailPrematch/clearAllDetail', null, { root: true })
        const previews = [...(rootGetters['previewPrematch/getTournamentsActive'] || [])]
        for (const source of previews) {
            await dispatch('previewPrematch/removeFromPreview', { source }, { root: true })
        }
        await dispatch('topsports/closePanel', null, { root: true })
        dispatch('tree/setStatusQbet', false, { root: true })
        commit('SET_TOURNAMENT_TAB', null)
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
